import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';
import { ErrorApi } from './errores';

/**
 * Cliente HTTP del facturador.
 *
 * Tres cosas que hace, y una que no:
 *
 * 1. **Está tipado por el contrato.** `createClient<paths>` toma los tipos
 *    generados desde `docs/openapi.yaml`: la ruta, los parámetros, el cuerpo
 *    del pedido y el de la respuesta salen de ahí. Si el backend renombra un
 *    campo, esto deja de compilar — que es exactamente el punto de KAN-141.
 * 2. **Agrega la credencial.** Un solo lugar pone el header `Authorization`.
 * 3. **Traduce los errores** a {@link ErrorApi} (ver {@link desenvolver}).
 *
 * Lo que NO hace: guardar la sesión. Este módulo no sabe dónde vive el token —
 * se lo pregunta a un proveedor que instala quien maneje la sesión (KAN-142,
 * donde ya está decidido que NO va a `localStorage`).
 */

/**
 * URL base: **el origen de la propia página, nunca un host escrito acá**.
 *
 * En desarrollo el dev server de Vite reenvía `/api` al backend
 * (ver `vite.config.ts`) y en producción el front se sirve detrás del mismo
 * origen que la API. Así el navegador nunca cruza de origen y **no hace falta
 * abrir CORS en un servicio fiscal**. Hardcodear `http://localhost:8080` acá
 * rompería las dos cosas: el día del despliegue el front le pegaría a la
 * máquina del visitante.
 *
 * Fuera del navegador (los tests) no hay página de la cual heredar el origen y
 * `new Request('/api/…')` no sabría contra qué resolver, así que se usa un
 * origen de relleno. Ahí el `fetch` está sustituido y no sale ningún pedido.
 */
function origenPropio(): string {
  const ubicacion = (globalThis as { location?: { origin?: string } }).location;
  return ubicacion?.origin ?? 'http://localhost';
}

/** Devuelve la credencial a mandar (JWT o API key), o `null` si no hay. */
export type ProveedorDeCredencial = () => string | null;

let credencial: ProveedorDeCredencial = () => null;

/**
 * Instala de dónde sale la credencial. La llama el dueño de la sesión al
 * arrancar la app; mientras nadie la llame, el cliente pide sin credencial y el
 * backend contesta `401 no_autenticado` (es deny-by-default).
 */
export function usarCredencial(proveedor: ProveedorDeCredencial): void {
  credencial = proveedor;
}

/** Olvida la credencial. Al cerrar sesión. */
export function olvidarCredencial(): void {
  credencial = () => null;
}

const autorizacion: Middleware = {
  onRequest({ request }) {
    // Si quien llama ya puso un Authorization explícito, se respeta: sirve para
    // probar una API key recién rotada sin tocar la sesión de la persona.
    if (request.headers.has('Authorization')) {
      return undefined;
    }
    const valor = credencial();
    if (valor) {
      request.headers.set('Authorization', `Bearer ${valor}`);
    }
    return undefined;
  },
};

/**
 * El cliente crudo. Devuelve `{ data, error, response }` sin lanzar nada;
 * envolvelo con {@link desenvolver} salvo que necesites la `Response` cruda
 * (por ejemplo para leer un header).
 */
export const api = createClient<paths>({
  baseUrl: origenPropio(),
  // openapi-fetch se queda con la referencia a `globalThis.fetch` en el momento
  // de crear el cliente. Resolverla en cada llamada cuesta nada y evita que el
  // cliente quede atado al `fetch` que existía al importar el módulo: es lo que
  // permite sustituirlo en los tests, y lo que haría falta el día que se quiera
  // instrumentar los pedidos.
  fetch: (pedido) => globalThis.fetch(pedido),
});
api.use(autorizacion);

/**
 * La forma de lo que devuelve una operación de openapi-fetch: `data` cuando
 * salió bien, `error` cuando el backend contestó con un error, y siempre la
 * `Response`. No se importa el tipo `FetchResponse` de la librería porque tiene
 * cinco parámetros genéricos y acá alcanza con esta estructura.
 */
type Resultado<T, E> = { data?: T; error?: E; response: Response };

/**
 * Convierte el resultado de una llamada en "o los datos, o un {@link ErrorApi}".
 *
 * ```ts
 * const sesion = await desenvolver(
 *   api.POST('/api/v1/auth/login', { body: { email, password } }),
 * );
 * ```
 *
 * Todo lo que puede salir mal termina siendo un `ErrorApi` con un **código**:
 * el del backend si hubo respuesta con el cuerpo del contrato, o uno local si
 * no la hubo. Quien llama nunca tiene que mirar `response.status` ni leer el
 * `detalle` para decidir.
 */
export async function desenvolver<T, E>(llamada: Promise<Resultado<T, E>>): Promise<T> {
  let resultado: Resultado<T, E>;
  try {
    resultado = await llamada;
  } catch (causa) {
    // fetch solo rechaza cuando no hubo respuesta: red caída, backend apagado,
    // pedido abortado. Un 500 NO cae acá.
    throw ErrorApi.deRed(causa);
  }
  if (!resultado.response.ok) {
    throw ErrorApi.deRespuesta(resultado.response.status, resultado.error);
  }
  // 204 y demás respuestas sin cuerpo llegan con `data` indefinido; el tipo
  // generado ya dice `never`/`undefined` en esos casos, así que quien llama no
  // puede usarlo sin darse cuenta.
  return resultado.data as T;
}
